import QrCodeIcon from '@mui/icons-material/QrCode';
import SecurityIcon from '@mui/icons-material/Security';
import {
  Box,
  fontSizes,
  GlassAlert,
  GlassButton,
  Link,
  MagicInput,
  ShimmerLoader,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import { type MultiFactorSession, multiFactor, TotpMultiFactorGenerator, type TotpSecret } from 'firebase/auth';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import env from '@/config/env';
import { auth } from '../config/firebase';
import { useTheme } from '../theme/ThemeProvider';

const DEFAULT_PORTAL_REDIRECTS: Record<string, string> = {
  admin: env.VITE_ADMIN_PORTAL_URL,
  business: env.VITE_BUSINESS_PORTAL_URL,
  consumer: env.VITE_CONSUMER_PORTAL_URL,
};

export default function MfaSetupPage() {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const { portalType, redirectUrl, tenantTheme } = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [secretKey, setSecretKey] = useState<string | null>(null);
  const [totpSecret, setTotpSecret] = useState<TotpSecret | null>(null);
  const [_multiFactorSession, setMultiFactorSession] = useState<MultiFactorSession | null>(null);

  const steps = [
    t('auth.mfa.setup.stepDownloadApp'),
    t('auth.mfa.setup.stepScanQr'),
    t('auth.mfa.setup.stepVerifyCode'),
  ];

  // Initialize MFA setup on mount
  useEffect(() => {
    const initializeMfa = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          setError(t('auth.mfa.setup.noUserError'));
          return;
        }

        // Get MFA session
        const session = await multiFactor(user).getSession();
        setMultiFactorSession(session);

        // Generate TOTP secret
        const secret = await TotpMultiFactorGenerator.generateSecret(session);
        setTotpSecret(secret);

        // Generate QR code URL for authenticator apps
        const qrUrl = secret.generateQrCodeUrl(
          user.email || 'user@example.com',
          tenantTheme?.companyName ?? 'Platform',
        );
        setQrCodeUrl(qrUrl);

        // Extract secret key for manual entry
        const secretKeyMatch = qrUrl.match(/secret=([A-Z2-7]+)/);
        if (secretKeyMatch?.[1]) {
          setSecretKey(secretKeyMatch[1]);
        }
      } catch (err) {
        console.error('MFA initialization error:', err);
        setError(t('auth.mfa.setup.initError'));
      }
    };

    initializeMfa();
  }, []);

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleVerify();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleVerify = async () => {
    if (verificationCode.length !== 6) {
      setError(t('auth.mfa.setup.enterSixDigit'));
      return;
    }

    if (!totpSecret || !auth.currentUser) {
      setError(t('auth.mfa.setup.notInitialized'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Create MFA assertion with the verification code
      const multiFactorAssertion = TotpMultiFactorGenerator.assertionForEnrollment(totpSecret, verificationCode);

      // Enroll the TOTP factor
      await multiFactor(auth.currentUser).enroll(multiFactorAssertion, 'Authenticator App');

      // Sync MFA status with backend
      const idToken = await auth.currentUser.getIdToken(true); // Force refresh to include MFA claim
      await fetch(`${import.meta.env.VITE_API_URL}/auth/mfa/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
      });

      // Navigate to appropriate location after setup
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        const destination = DEFAULT_PORTAL_REDIRECTS[portalType ?? 'business'] ?? env.VITE_BUSINESS_PORTAL_URL;
        window.location.href = destination;
      }
    } catch (err) {
      console.error('MFA setup error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to verify code';
      if (errorMessage.includes('invalid-verification-code')) {
        setError(t('auth.mfa.setup.invalidCode'));
      } else {
        setError(t('auth.mfa.setup.setupError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const currentParams = searchParams.toString();
  const queryString = currentParams ? `?${currentParams}` : '';

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body1" gutterBottom>
              {t('auth.mfa.setup.downloadInstruction')}
            </Typography>
            <Box sx={{ my: 3 }}>
              <Typography variant="body2" color="textSecondary">
                {`\u2022 ${t('auth.mfa.setup.googleAuthenticator')}`}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {`\u2022 ${t('auth.mfa.setup.microsoftAuthenticator')}`}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {`\u2022 ${t('auth.mfa.setup.authy')}`}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {`\u2022 ${t('auth.mfa.setup.onePassword')}`}
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary">
              {t('auth.mfa.setup.totpCompatible')}
            </Typography>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body1" gutterBottom>
              {t('auth.mfa.setup.scanInstruction')}
            </Typography>
            <Box
              sx={{
                my: 3,
                p: 3,
                bgcolor: 'background.paper',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              {qrCodeUrl ? (
                <>
                  {/* Display QR code using otpauth:// URL */}
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'white',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {/* Note: Firebase doesn't generate actual QR image, just otpauth URL */}
                    {/* For production, use a QR library like qrcode.react or display the URL */}
                    <QrCodeIcon sx={{ fontSize: 150, color: 'grey.400' }} />
                  </Box>
                  {secretKey && (
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }} gutterBottom>
                        {t('auth.mfa.setup.manualEntryKey')}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          bgcolor: 'grey.100',
                          p: 1,
                          borderRadius: 1,
                          userSelect: 'all',
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: 'grey.200',
                          },
                        }}
                        onClick={() => navigator.clipboard.writeText(secretKey)}
                        title={t('common.copy')}
                      >
                        {secretKey}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                        {t('auth.mfa.setup.clickToCopy')}
                      </Typography>
                    </Box>
                  )}
                </>
              ) : (
                <Box
                  sx={{
                    width: 200,
                    height: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'background.default',
                    borderRadius: 1,
                  }}
                >
                  <ShimmerLoader variant="rectangular" width={200} height={200} />
                </Box>
              )}
            </Box>
            <Typography variant="body2" color="textSecondary">
              {secretKey ? t('auth.mfa.setup.scanOrManual') : t('auth.mfa.setup.loadingQr')}
            </Typography>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body1" gutterBottom>
              {t('auth.mfa.setup.enterCode')}
            </Typography>
            <MagicInput
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              slotProps={{
                htmlInput: {
                  maxLength: 6,
                  style: {
                    textAlign: 'center',
                    fontSize: fontSizes['2xl'],
                    fontWeight: 600,
                    letterSpacing: '0.5em',
                  },
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                },
              }}
              sx={{ my: 3, width: 200, mx: 'auto' }}
            />
            <Typography variant="body2" color="textSecondary">
              {t('auth.mfa.setup.codeChanges')}
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <SecurityIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }} gutterBottom>
          {t('auth.mfa.setup.title')}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {t('auth.mfa.setup.subtitle')}
        </Typography>
      </Box>

      <GlassAlert severity="error" visible={!!error} sx={{ mb: 3 }}>
        {error}
      </GlassAlert>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {renderStepContent(activeStep)}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <GlassButton disabled={activeStep === 0} onClick={handleBack}>
          {t('common.back')}
        </GlassButton>
        <GlassButton
          variant="subtle"
          onClick={handleNext}
          disabled={isLoading || (activeStep === 2 && verificationCode.length !== 6)}
        >
          {isLoading ? (
            <ShimmerLoader variant="circular" width={24} height={24} />
          ) : activeStep === steps.length - 1 ? (
            t('auth.mfa.setup.completeSetup')
          ) : (
            t('common.next')
          )}
        </GlassButton>
      </Box>

      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Link component={RouterLink} to={`/login${queryString}`} variant="body2" color="text.secondary">
          {t('auth.mfa.setup.skipForNow')}
        </Link>
      </Box>
    </Box>
  );
}
